<template>
    <div class="card h-100 mb-2">
        <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h3 class="card-title mb-0">Andamento del periodo</h3>
        </div>
        <div class="card-body">
            <div v-if="loading" class="placeholder-glow text-center py-5">
                <span class="placeholder col-12 placeholder-lg"></span>
            </div>

            <BlockState v-else-if="nonDisponibile"
                        stato="non_disponibile"
                        dettaglio="Il trend non è disponibile per questa richiesta." />

            <p v-else-if="vuoto" class="text-muted text-center mb-0">Nessun dato nel periodo selezionato.</p>

            <template v-else>
                <!-- UI-04C: combinato finanziario. Fatturato e Costi sono due
                     serie BAR affiancate, Margine % e' una LINEA sul secondo asse Y.
                     La legenda ECharts in alto e' l'unico controllo: comportamento
                     standard, cliccabile per mostrare/nascondere una serie.
                     Nessun selettore custom, nessuna nuova API. -->
                <!-- Il contenitore ha altezza esplicita e ruolo accessibile; il
                     wrapper Vue ECharts inoltra class/style/attrs al nodo radice. -->
                <div class="trend-chart" role="img" :aria-label="descrizioneAccessibile">
                    <VChart class="trend-chart-canvas"
                            :option="chartOption"
                            :update-options="opzioniAggiornamento"
                            autoresize />
                </div>
                <!-- Fallback testuale: il grafico non e' l'unico modo di leggere il dato. -->
                <details class="mt-2">
                    <summary class="text-muted small">Dati in forma testuale</summary>
                    <div class="table-responsive mt-2">
                        <table class="table table-sm font-size-sm mb-0">
                            <caption class="visually-hidden">Andamento mensile: fatturato, costi e margine</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Mese</th>
                                    <th scope="col" class="text-end">Fatturato</th>
                                    <th scope="col" class="text-end">Costi</th>
                                    <th scope="col" class="text-end">Margine</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="r in righeTesto" :key="r.label">
                                    <th scope="row">{{ r.label }}</th>
                                    <td class="text-end">{{ r.fatturato }}</td>
                                    <td class="text-end">{{ r.costi }}</td>
                                    <td class="text-end">{{ r.margine }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </details>
            </template>
        </div>
    </div>
</template>

<script>
/* FINAL-TC-VUE + UI-04C - TrendChart su Vue ECharts (https://vue-echarts.dev/).
 *
 * Il disegno del grafico e' interamente delegato al wrapper <VChart>:
 * init/dispose/setOption appartengono al suo lifecycle e nessun oggetto ECharts
 * viene conservato in data().
 * Non esiste alcun osservatore di layout, timer, retry, sonda su canvas o ZRender,
 * ne' alcuna diagnostica temporanea.
 *
 * UI-04C: FINANCIAL COMBO. Fatturato e Costi sono due serie BAR affiancate
 * (nessuno stack), Margine % e' una LINEA su un secondo asse Y. Margine % e'
 * derivato dal margine canonico del payload: (margine / fatturato) * 100, con
 * 0 quando il fatturato e' 0 (stessa definizione gia' usata dalla Dashboard).
 * Il Margine in euro compare nel TOOLTIP (valore canonico del payload), non come
 * serie. Nessun area fill, nessun Budget, nessun confronto con l'anno precedente.
 */
import BlockState from "./BlockState.vue";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { LineChart, BarChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

/* Import mirato (tree-shaking): solo i moduli ECharts usati dalla option. */
use([CanvasRenderer, LineChart, BarChart, GridComponent, TooltipComponent, LegendComponent]);

const FORMAT_EUR = new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
});
/* Asse Y sinistro: importi arrotondati, niente decimali inutili. */
const FORMAT_EUR_ASSE = new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
});
/* Asse Y destro: percentuale intera. La serie contiene 0..100, non 0..1. */
const FORMAT_PERCENT_ASSE = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });
const FORMAT_PERCENT = new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
});

/* Palette coerente con la Dashboard. */
const COL_FATTURATO = "#3b82f6";   /* blu   */
const COL_COSTI = "#16a34a";       /* verde soft */
const COL_MARGINE_PCT = "#f59e0b"; /* amber */

/* Chiavi REALI del contratto V1 (trend.corrente[key]): nessuna formula
   economica viene ricalcolata, a parte Margine %. */
const CHIAVE_FATTURATO = "fatturato";
const CHIAVE_COSTI = "costi";
const CHIAVE_MARGINE = "margine";

/* Etichette dei mesi: solo presentazione. Il dato resta la label reale
   dal payload (YYYY-MM); la tabella la abbrevia per l'asse. */
const MESI_BREVI = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];
const MESI_LUNGHI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default {
    name: "TrendChart",
    components: { BlockState, VChart },

    props: {
        trend: { type: Object, default: null },
        loading: { type: Boolean, default: false },
    },

    computed: {
        nonDisponibile() {
            return !this.loading && (this.trend === null || this.trend === undefined);
        },
        vuoto() {
            /* Guardia esplicita su trend: puo' essere valutato mentre trend e' null. */
            if (!this.trend) return true;
            return !Array.isArray(this.trend.labels) || this.trend.labels.length === 0;
        },
        etichette() {
            const s = (this.trend && this.trend.labels);
            return Array.isArray(s) ? s : [];
        },
        /* Serie V1 correnti, lette con la chiave reale. Lettura difensiva:
           se manca o non e' array -> [] (mai undefined). */
        serieV1() {
            const corrente = (this.trend && this.trend.corrente) || {};
            return [CHIAVE_FATTURATO, CHIAVE_COSTI, CHIAVE_MARGINE].map((k) => ({
                key: k,
                dati: Array.isArray(corrente[k]) ? corrente[k] : [],
            }));
        },
        righeTesto() {
            const mesi = (this.trend && Array.isArray(this.trend.mesi)) ? this.trend.mesi : [];
            const perChiave = {};
            this.serieV1.forEach((s) => { perChiave[s.key] = s.dati; });
            return mesi.map((m, i) => ({
                label: this.etichette[i] || m,
                fatturato: FORMAT_EUR.format(perChiave.fatturato[i] || 0),
                costi: FORMAT_EUR.format(perChiave.costi[i] || 0),
                margine: FORMAT_EUR.format(perChiave.margine[i] || 0),
            }));
        },
        descrizioneAccessibile() {
            return "Combinato finanziario mensile: barre di fatturato e costi, "
                + "linea del margine percentuale. "
                + "I valori di ogni mese sono disponibili nella tabella testuale.";
        },
        /* Sostituzione completa dell'option: nessuna serie obsoleta sopravvive. */
        opzioniAggiornamento() {
            return { notMerge: true };
        },

        /* Tutta la configurazione ECharts vive qui: deriva dalle prop reali, quindi il
           wrapper aggiorna il grafico da solo quando i dati cambiano. */
        chartOption() {
            const perChiave = {};
            this.serieV1.forEach((s) => { perChiave[s.key] = s.dati; });
            return {
                animation: false,
                legend: {
                    /* In alto, orizzontale e centrata. Comportamento standard ECharts:
                       il click su una voce mostra/nasconde la serie. */
                    top: 0,
                    left: "center",
                    itemGap: 20,
                    icon: "roundRect",
                    itemWidth: 14,
                    itemHeight: 8,
                    textStyle: { color: "#334155", fontSize: 12 },
                    selectedMode: true,
                    data: ["Fatturato", "Costi", "Margine %"],
                },
                grid: { left: 8, right: 8, top: 46, bottom: 6, containLabel: true },
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    formatter: this.formatterTooltip,
                },
                xAxis: {
                    type: "category",
                    data: this.etichette,
                    axisLabel: { color: "#64748b", fontSize: 11, formatter: this.meseBreve },
                    axisLine: { lineStyle: { color: "#e2e8f0" } },
                    axisTick: { show: false },
                    /* Verticali assenti: solo la griglia orizzontale, come in reference. */
                    splitLine: { show: false },
                },
                /* Asse sinistro: EUR, condiviso da Fatturato e Costi. */
                yAxis: [
                    {
                        type: "value",
                        name: "Valore (\u20ac)",
                        nameTextStyle: { color: "#94a3b8", fontSize: 10, padding: [0, 0, 0, 24] },
                        axisLabel: { color: "#64748b", fontSize: 11, formatter: (v) => FORMAT_EUR_ASSE.format(v) },
                        axisLine: { show: false },
                        axisTick: { show: false },
                        splitLine: { show: true, lineStyle: { color: "#eef2f7" } },
                    },
                    {
                        /* Asse destro: Margine %. Il range lo calcola ECharts sui dati
                           reali (niente max arbitrario). */
                        type: "value",
                        name: "Margine (%)",
                        nameTextStyle: { color: "#94a3b8", fontSize: 10 },
                        position: "right",
                        axisLabel: { color: "#64748b", fontSize: 11, formatter: (v) => FORMAT_PERCENT_ASSE.format(v) + " %" },
                        axisLine: { show: false },
                        axisTick: { show: false },
                        splitLine: { show: false },
                    },
                ],
                series: [
                    {
                        /* Fatturato: barra, asse sinistro. */
                        name: "Fatturato", type: "bar", yAxisIndex: 0,
                        data: perChiave[CHIAVE_FATTURATO],
                        barMaxWidth: 18, barGap: "12%",
                        itemStyle: { color: COL_FATTURATO, borderRadius: [3, 3, 0, 0] },
                        emphasis: { focus: "series" },
                    },
                    {
                        /* Costi: barra, asse sinistro, AFFIANCATA (nessuno stack). */
                        name: "Costi", type: "bar", yAxisIndex: 0,
                        data: perChiave[CHIAVE_COSTI],
                        barMaxWidth: 18,
                        itemStyle: { color: COL_COSTI, borderRadius: [3, 3, 0, 0] },
                        emphasis: { focus: "series" },
                    },
                    {
                        /* Margine %: linea, asse destro. Nessun area fill. */
                        name: "Margine %", type: "line", yAxisIndex: 1,
                        data: this.marginiPercentuali(),
                        smooth: false,
                        showSymbol: true,
                        symbol: "circle",
                        symbolSize: 6,
                        lineStyle: { width: 3, color: COL_MARGINE_PCT },
                        itemStyle: { color: COL_MARGINE_PCT },
                        z: 5,
                    },
                ],
            };
        },
    },

    methods: {
        /* Valore canonico del payload per chiave e indice (0 se assente). */
        valore(chiave, i) {
            const perChiave = {};
            this.serieV1.forEach((s) => { perChiave[s.key] = s.dati; });
            const arr = perChiave[chiave] || [];
            const v = arr[i];
            return (typeof v === "number" && isFinite(v)) ? v : 0;
        },
        /* MARGINE % = (margine / fatturato) * 100.
           Se fatturato == 0 -> 0 (nessuna divisione per zero).
           Il margine in euro NON e' ricalcolato: si usa il dato canonico. */
        marginePercentuale(i) {
            const fatturato = this.valore(CHIAVE_FATTURATO, i);
            if (!fatturato) return 0;
            return (this.valore(CHIAVE_MARGINE, i) / fatturato) * 100;
        },
        /* Serie dei margini percentuali, un valore per ogni mese reale. */
        marginiPercentuali() {
            return this.etichette.map((_, i) => this.marginePercentuale(i));
        },
        /* '2026-03' -> indice 2 (fallback: nessuna corrispondenza). */
        indiceMese(etichetta) {
            const m = /^(\d{4})-(\d{2})$/.exec(String(etichetta || ""));
            if (!m) return -1;
            return Math.min(11, Math.max(0, parseInt(m[2], 10) - 1));
        },
        /* Label breve per l'asse X; il dato resta la label reale dal payload. */
        meseBreve(etichetta) {
            const i = this.indiceMese(etichetta);
            return i >= 0 ? MESI_BREVI[i] : etichetta;
        },
        /* Titolo del tooltip: mese esteso + anno reale. */
        meseLungo(etichetta) {
            const i = this.indiceMese(etichetta);
            if (i < 0) return String(etichetta || "");
            const anno = /^(\d{4})-/.exec(String(etichetta || ""));
            return MESI_LUNGHI[i] + (anno ? " " + anno[1] : "");
        },
        /* Tooltip: mese reale + Fatturato, Costi, Margine (€) e Margine %.
           Il Margine in euro NON e' ricalcolato: si legge il valore canonico
           del payload nella posizione del mese. */
        formatterTooltip(params) {
            if (!Array.isArray(params) || params.length === 0) return "";
            const primo = params[0] || {};
            const etichetta = primo.axisValue;
            const i = this.etichette.indexOf(etichetta);
            const idx = (i >= 0) ? i : 0;
            const riga = (nome, valore) =>
                '<div style="display:flex;justify-content:space-between;gap:18px">'
                + "<span>" + nome + "</span><span>" + valore + "</span></div>";
            return '<div style="font-weight:600;margin-bottom:3px;text-transform:uppercase">'
                + this.meseLungo(etichetta) + "</div>"
                + riga("Fatturato", FORMAT_EUR.format(this.valore(CHIAVE_FATTURATO, idx)))
                + riga("Costi", FORMAT_EUR.format(this.valore(CHIAVE_COSTI, idx)))
                + riga("Margine", FORMAT_EUR.format(this.valore(CHIAVE_MARGINE, idx)))
                + riga("Margine %", FORMAT_PERCENT.format(this.marginePercentuale(idx)) + " %");
        },
    },
};
</script>

<style scoped>
/* Dimensioni esplicite e stabili: il wrapper Vue ECharts non include CSS propri. */
.trend-chart {
    width: 100%;
    height: 320px;
}
.trend-chart-canvas {
    width: 100%;
    height: 100%;
}
</style>
